import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FiX, FiRefreshCw } from 'react-icons/fi';

const selectClass = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400';
const labelClass = 'block text-xs font-medium text-slate-500 mb-1';

const QuestionFilters = ({ filters, onFilterChange, onClose }) => {
  const { api } = useAuth();
  const [localFilters, setLocalFilters] = useState(filters);
  const [filterOptions, setFilterOptions] = useState({
    subjects: [],
    examTypes: [],
    topics: [],
    authors: [],
    classes: []
  });
  const [loading, setLoading] = useState(true);

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        const response = await api.get('/questions/filter-options');
        setFilterOptions(response.data.data || {
          subjects: [],
          examTypes: [],
          topics: [],
          authors: [],
          classes: []
        });
      } catch (err) {
        console.error('Error fetching filter options:', err);
        // Set empty arrays as fallback
        setFilterOptions({
          subjects: [],
          examTypes: [],
          topics: [],
          authors: [],
          classes: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFilterOptions();
  }, [api]);

  // Handle local filter change
  const handleLocalFilterChange = (key, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  // Reset filters
  const handleResetFilters = () => {
    const resetFilters = {
      subject: '',
      examType: '',
      class: '',
      difficulty: '',
      topic: '',
      status: '',
      createdBy: '',
      dateRange: '',
      qualityScore: '',
      timesUsed: '',
      hasExplanation: '',
      hasMedia: ''
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  // Check if filters are applied
  const hasActiveFilters = Object.entries(localFilters).some(([key, value]) => {
    return value && value !== '';
  });

  const difficultyOptions = [
    { value: '', label: 'All Difficulties' },
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' },
    { value: 'archived', label: 'Archived' }
  ];

  const dateRangeOptions = [
    { value: '', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  const qualityScoreOptions = [
    { value: '', label: 'Any Quality' },
    { value: 'high', label: 'High Quality (80%+)' },
    { value: 'medium', label: 'Medium Quality (60-79%)' },
    { value: 'low', label: 'Low Quality (<60%)' }
  ];

  const usageOptions = [
    { value: '', label: 'Any Usage' },
    { value: 'unused', label: 'Never Used' },
    { value: 'low', label: 'Low Usage (1-10)' },
    { value: 'medium', label: 'Medium Usage (11-50)' },
    { value: 'high', label: 'High Usage (50+)' }
  ];

  return (
    <div className="border-t border-slate-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/50">
        <h3 className="text-sm font-medium text-slate-900">Filter questions</h3>
        <button type="button" onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:bg-slate-200/60 transition-colors" aria-label="Close"><FiX className="h-4 w-4" /></button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="text-center py-8 text-sm text-slate-500">Loading filter options…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Subject</label>
              <select value={localFilters.subject || ''} onChange={(e) => handleLocalFilterChange('subject', e.target.value)} className={selectClass}>
                <option value="">All subjects</option>
                {(filterOptions.subjects || []).map((subject) => (
                  <option key={subject} value={subject.toLowerCase()}>{subject}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Exam type</label>
              <select value={localFilters.examType || ''} onChange={(e) => handleLocalFilterChange('examType', e.target.value)} className={selectClass}>
                <option value="">All exam types</option>
                {(filterOptions.examTypes || []).map((examType) => (
                  <option key={examType} value={examType.toLowerCase()}>{examType}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Class</label>
              <select value={localFilters.class || ''} onChange={(e) => handleLocalFilterChange('class', e.target.value)} className={selectClass}>
                <option value="">All classes</option>
                {(filterOptions.classes || []).map((classLevel) => (
                  <option key={classLevel} value={classLevel}>{classLevel}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Difficulty</label>
              <select value={localFilters.difficulty || ''} onChange={(e) => handleLocalFilterChange('difficulty', e.target.value)} className={selectClass}>
                {difficultyOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Topic</label>
              <select value={localFilters.topic || ''} onChange={(e) => handleLocalFilterChange('topic', e.target.value)} className={selectClass}>
                <option value="">All topics</option>
                {(filterOptions.topics || []).map((topic) => (
                  <option key={topic} value={topic}>{topic}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select value={localFilters.status || ''} onChange={(e) => handleLocalFilterChange('status', e.target.value)} className={selectClass}>
                {statusOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Created by</label>
              <select value={localFilters.createdBy || ''} onChange={(e) => handleLocalFilterChange('createdBy', e.target.value)} className={selectClass}>
                <option value="">All authors</option>
                {(filterOptions.authors || []).map((author) => (
                  <option key={author._id || author.id} value={author._id || author.id}>{author.name || author.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Date created</label>
              <select value={localFilters.dateRange || ''} onChange={(e) => handleLocalFilterChange('dateRange', e.target.value)} className={selectClass}>
                {dateRangeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Quality score</label>
              <select value={localFilters.qualityScore || ''} onChange={(e) => handleLocalFilterChange('qualityScore', e.target.value)} className={selectClass}>
                {qualityScoreOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Usage count</label>
              <select value={localFilters.timesUsed || ''} onChange={(e) => handleLocalFilterChange('timesUsed', e.target.value)} className={selectClass}>
                {usageOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className={labelClass}>Additional</label>
              <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={localFilters.hasExplanation === 'true'} onChange={(e) => handleLocalFilterChange('hasExplanation', e.target.checked ? 'true' : '')} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
                  Has explanation
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={localFilters.hasMedia === 'true'} onChange={(e) => handleLocalFilterChange('hasMedia', e.target.checked ? 'true' : '')} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
                  Has media
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50/50">
        <div className="text-sm text-slate-500">
          {hasActiveFilters && <span>{Object.entries(localFilters).filter(([k, v]) => v && v !== '').length} filters active</span>}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={handleResetFilters} disabled={!hasActiveFilters} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"><FiRefreshCw className="h-4 w-4" /> Reset</button>
          <button type="button" onClick={handleApplyFilters} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">Apply filters</button>
        </div>
      </div>
    </div>
  );
};

export default QuestionFilters;